/**
 * 3D Planet — add-on-checkbox in het koopblok (blocks/tdp-addon-checkbox.liquid).
 *
 * Zolang het vinkje aan staat onderschept dit element de submit van het Horizon-productformulier
 * (`product-form-component.handleSubmit`, opgezocht per event via `on:submit="/handleSubmit"`) en
 * stuurt hoofdproduct + add-on in één /cart/add.js-call, gekoppeld via `_addon_for`
 * (assets/quote-addons.js). Daarna wordt dezelfde CartLinesUpdateEvent gestuurd als Horizon zelf,
 * zodat de cart-drawer opent en ververst. Vinkje uit = origineel Horizon-gedrag.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ADDON_QUANTITY = 1;

class TdpAddonCheckbox extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {Promise<typeof import('./quote-addons.js')> | null} */
  #module = null;
  #patched = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.input?.addEventListener('change', () => this.#onToggle(), { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => this.#onCartUpdate(event), { signal });
    this.#patchForm();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-tdp-addon-input]'));
  }

  get formComponent() {
    const form = document.getElementById(this.dataset.formId || '');
    return form?.closest('product-form-component') || null;
  }

  #loadModule() {
    if (!this.#module) this.#module = import(this.dataset.addonsSrc || '');
    return this.#module;
  }

  /** Schaduwt `handleSubmit` op de instantie; Horizon zoekt de methode per event op (`instance[method]`). */
  #patchForm() {
    const component = this.formComponent;
    if (!component || this.#patched) return;
    const original = Object.getPrototypeOf(component).handleSubmit;
    if (typeof original !== 'function') return;
    component.handleSubmit = (event) => {
      if (this.input?.checked && !this.input.disabled) {
        event.preventDefault();
        this.#submitWithAddon(component, event).catch((error) => this.#showError(error));
        return;
      }
      return original.call(component, event);
    };
    this.#patched = true;
  }

  #onToggle() {
    this.#patchForm();
    if (this.input?.checked) this.#loadModule().catch((error) => this.#showError(error));
    this.#showError(null);
  }

  /** Na een succesvolle toevoeging (door wie dan ook) staat de add-on 1× per bestelling in de cart. */
  #onCartUpdate(event) {
    event.promise?.then(({ detail }) => {
      const items = detail?.items;
      if (!Array.isArray(items)) return;
      const inCart = items.some((item) => item.handle === this.dataset.addonHandle);
      this.#setInCart(inCart);
    }).catch(() => {});
  }

  #setInCart(inCart) {
    const status = this.querySelector('[data-tdp-addon-status]');
    if (!this.input) return;
    this.input.disabled = inCart;
    if (inCart) this.input.checked = false;
    status?.toggleAttribute('hidden', !inCart);
  }

  /** @param {HTMLElement} component  @param {Event} event */
  async #submitWithAddon(component, event) {
    const form = component.querySelector('form');
    if (!(form instanceof HTMLFormElement)) throw new Error('Productformulier ontbreekt');
    const { buildCartPayload, cartDisplayOrder } = await this.#loadModule();
    const formData = new FormData(form);
    const mainLine = {
      id: formData.get('id'),
      quantity: Number(formData.get('quantity')) || Number(component.dataset.quantityDefault) || 1,
      properties: Object.fromEntries(
        [...formData.entries()]
          .filter(([name, value]) => name.startsWith('properties[') && typeof value === 'string' && value !== '')
          .map(([name, value]) => [name.slice('properties['.length, -1), value]),
      ),
    };
    const payload = buildCartPayload(mainLine, [
      { variantId: this.dataset.variantId, quantity: ADDON_QUANTITY, label: this.dataset.productTitle },
    ]);

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    component.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: payload.items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      }),
    );

    try {
      const body = JSON.stringify({ items: cartDisplayOrder(payload.items), sections: sections.join(',') });
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, fetchConfig('json', { body }));
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Toevoegen aan de winkelwagen is mislukt');
      }
      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: payload.items.reduce((sum, item) => sum + item.quantity, 0),
          source: 'tdp-addon-checkbox',
          sourceId: this.id,
          productId: component.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.#setInCart(true);
    } catch (error) {
      deferred.reject(error);
      component.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Add to cart failed', code: 'INVALID' }));
      throw error;
    }
  }

  /** @param {unknown} error */
  #showError(error) {
    const target = this.querySelector('[data-tdp-addon-error]');
    if (!target) return;
    if (!error) {
      target.setAttribute('hidden', '');
      return;
    }
    console.error('[tdp-addon-checkbox]', error);
    target.textContent = error instanceof Error && error.message ? error.message : 'Toevoegen aan de winkelwagen is mislukt. Probeer het opnieuw.';
    target.removeAttribute('hidden');
  }
}

if (!customElements.get('tdp-addon-checkbox')) {
  customElements.define('tdp-addon-checkbox', TdpAddonCheckbox);
}
